import Big from 'big.js';
import { AMOUNT_FORMAT, toExcelDate } from 'components/xlsx/excelCells';
import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { OpenAdvanceRow } from 'types/reports';
import { t } from 'utils/getTranslations';

// big.js rounds HALF_UP by default (Big.RM === Big.roundHalfUp); the Net/VAT split below relies on it.

export interface DerivedOpenAdvance {
    customer: string;
    advanceNumber: string;
    paidOn: string;
    gross: number;
    used: number;
    net: number;
    vat: number;
    remaining: number;
    vatRate: number;
    vatLabel: string;
    matter: string;
    usedInvoices: string;
}

const round2 = (value: Big): number => Number(value.round(2).toString());

/**
 * Splits a row's Remaining balance into Net / VAT using the advance's own VAT rate
 * (rate = VAT total / Net total). Rounding is HALF_UP to 2 decimal places, matching portal-be.
 */
export const deriveOpenAdvance = (row: OpenAdvanceRow): DerivedOpenAdvance => {
    const net = new Big(row.netTotalBaseCurrency || 0);
    const rate = net.eq(0) ? new Big(0) : new Big(row.vatTotalBaseCurrency || 0).div(net);
    const remaining = new Big(row.remainingAmountBaseCurrency || 0);
    const derivedNet = remaining.div(rate.plus(1));
    const derivedVat = remaining.minus(derivedNet);

    return {
        customer: row.clientPartyName,
        advanceNumber: row.invoiceNumber,
        paidOn: row.paidOnDate,
        gross: round2(new Big(row.grossTotalBaseCurrency || 0)),
        used: round2(new Big(row.usedAmountBaseCurrency || 0)),
        net: round2(derivedNet),
        vat: round2(derivedVat),
        remaining: round2(remaining),
        vatRate: round2(rate.times(100)),
        vatLabel: `${round2(rate.times(100))}%`,
        matter: row.suggestedMatters,
        usedInvoices: row.usedInvoiceNumbers,
    };
};

export interface VatSpecificationLine {
    vatLabel: string;
    net: number;
    vat: number;
    remaining: number;
}

/** Groups derived rows by VAT % and sums Net / VAT / Remaining within each group. */
export const buildVatSpecification = (rows: DerivedOpenAdvance[]): VatSpecificationLine[] => {
    const byRate = new Map<string, VatSpecificationLine>();
    rows.forEach((row) => {
        const existing = byRate.get(row.vatLabel) ?? { vatLabel: row.vatLabel, net: 0, vat: 0, remaining: 0 };
        existing.net = round2(new Big(existing.net).plus(row.net));
        existing.vat = round2(new Big(existing.vat).plus(row.vat));
        existing.remaining = round2(new Big(existing.remaining).plus(row.remaining));
        byRate.set(row.vatLabel, existing);
    });
    return [...byRate.values()].sort((a, b) => Number.parseFloat(a.vatLabel) - Number.parseFloat(b.vatLabel));
};

const sum = (values: number[]): number => round2(values.reduce((acc, value) => acc.plus(value), new Big(0)));

// Zero-based column indexes (within the 11-column data table) whose cells hold money.
const MONEY_COLUMN_INDEXES = [3, 4, 5, 6, 7];
// Sensible per-column widths for the data table, matched to the column set below.
const DATA_COLUMN_WIDTHS = [28, 16, 12, 12, 12, 12, 12, 14, 12, 28, 22];
// Column widths for the standalone VAT-specification block.
const VAT_SPEC_COLUMN_WIDTHS = [20, 14, 14, 14];

const applyMoneyFormat = (row: ExcelJS.Row, columnIndexes: number[]): void => {
    columnIndexes.forEach((index) => {
        row.getCell(index + 1).numFmt = AMOUNT_FORMAT;
    });
};

/** Builds and downloads the Open Advances workbook: data rows, a grand-total row, and a VAT-specification block. */
export const exportOpenAdvancesXlsx = async (rows: OpenAdvanceRow[], asOfDate: string): Promise<void> => {
    const derived = rows.map(deriveOpenAdvance);

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('reports.open-advances.sheet-name'));

    const header = [
        t('reports.open-advances.column.customer'),
        t('reports.open-advances.column.advance-number'),
        t('reports.open-advances.column.paid-on'),
        t('reports.open-advances.column.gross'),
        t('reports.open-advances.column.used'),
        t('reports.open-advances.column.net'),
        t('reports.open-advances.column.vat'),
        t('reports.open-advances.column.remaining'),
        t('reports.open-advances.column.vat-percent'),
        t('reports.open-advances.column.matter'),
        t('reports.open-advances.column.used-invoices'),
    ];

    const headerRow = worksheet.addRow(header);
    headerRow.font = { bold: true };

    derived.forEach((row) => {
        const dataRow = worksheet.addRow([
            row.customer,
            row.advanceNumber,
            toExcelDate(row.paidOn),
            row.gross,
            row.used,
            row.net,
            row.vat,
            row.remaining,
            row.vatLabel,
            row.matter,
            row.usedInvoices,
        ]);
        applyMoneyFormat(dataRow, MONEY_COLUMN_INDEXES);
    });

    const totalRow = worksheet.addRow([
        t('reports.open-advances.total'),
        '',
        '',
        sum(derived.map((row) => row.gross)),
        sum(derived.map((row) => row.used)),
        sum(derived.map((row) => row.net)),
        sum(derived.map((row) => row.vat)),
        sum(derived.map((row) => row.remaining)),
        '',
        '',
        '',
    ]);
    totalRow.font = { bold: true };
    applyMoneyFormat(totalRow, MONEY_COLUMN_INDEXES);

    DATA_COLUMN_WIDTHS.forEach((width, index) => {
        worksheet.getColumn(index + 1).width = width;
    });

    // Blank separator row between the data table and the VAT-specification block.
    worksheet.addRow([]);

    const vatSpecHeader = worksheet.addRow([
        t('reports.open-advances.vat-specification'),
        t('reports.open-advances.column.net'),
        t('reports.open-advances.column.vat'),
        t('reports.open-advances.column.remaining'),
    ]);
    vatSpecHeader.font = { bold: true };

    // The VAT-specification block reuses columns 1-4; widen them to at least the spec widths.
    VAT_SPEC_COLUMN_WIDTHS.forEach((width, index) => {
        const column = worksheet.getColumn(index + 1);
        column.width = Math.max(column.width ?? 0, width);
    });

    const vatSpecification = buildVatSpecification(derived);
    vatSpecification.forEach((line) => {
        const specRow = worksheet.addRow([line.vatLabel, line.net, line.vat, line.remaining]);
        // Net / VAT / Remaining sit in columns 2-4 (zero-based 1-3) in this block.
        applyMoneyFormat(specRow, [1, 2, 3]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `open-advances_${asOfDate}.xlsx`);
};
